// Game Voting Screen - Players rank top 3 games per category before schedule is set
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompetition } from '../contexts/CompetitionContext';
import { Button, Card } from '../components';
import {
  savePlayerVotes,
  submitPlayerVotes,
  getPlayerVotes,
  checkAllPlayersVoted,
  getVotingResultsWithGameDetails,
  getVotingStatus,
  closeVoting,
  confirmSchedule,
  reopenVoting,
  deleteAllVotesForCompetition,
  deletePlayerVote
} from '../services/gameVotingService';
import { getGameSectionsForLeague } from '../services/gameSectionService';
import { getCategoriesForCompetition } from '../services/categoryService';

const GameVoting = () => {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { competition, players, isOrganizer, refreshCompetition, currentPlayer } = useCompetition();

  const [gameSections, setGameSections] = useState([]);
  const [categories, setCategories] = useState([]);
  // votes format: { categoryId: [gameSectionId1, gameSectionId2, gameSectionId3] }
  const [votes, setVotes] = useState({});
  const [votingStatus, setVotingStatus] = useState(null);
  const [votingResults, setVotingResults] = useState(null);
  const [allPlayersStatus, setAllPlayersStatus] = useState({ allVoted: false, votedCount: 0, totalPlayers: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [resetting, setResetting] = useState(false);

  // Get player ID from currentPlayer (from CompetitionContext)
  const playerId = currentPlayer?.odplayerId || currentPlayer?.id;
  const playerName = currentPlayer?.displayName || user?.displayName || 'Unknown';

  // Group games by category (matching by category name since library uses categoryName)
  const gamesByCategory = useMemo(() => {
    const grouped = {};
    categories.forEach(cat => {
      // Match library games to competition categories by name (case-insensitive)
      grouped[cat.id] = gameSections.filter(g => {
        const gameCategoryName = (g.categoryName || '').toLowerCase().trim();
        const competitionCategoryName = (cat.name || '').toLowerCase().trim();
        return gameCategoryName === competitionCategoryName;
      });
    });
    return grouped;
  }, [gameSections, categories]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (!competition?.leagueId || !playerId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Load game sections for the league
        const sections = await getGameSectionsForLeague(competition.leagueId);
        setGameSections(sections);

        // Load categories from competition
        const cats = await getCategoriesForCompetition(competitionId);
        const activeCats = cats.filter(c => c.status === 'active');
        setCategories(activeCats);

        if (activeCats.length > 0) {
          setActiveCategory(activeCats[0].id);
        }

        // Load voting status
        const status = await getVotingStatus(competitionId);
        setVotingStatus(status);

        // Load player's existing votes
        const playerVotes = await getPlayerVotes(competitionId, playerId);
        if (playerVotes) {
          setVotes(playerVotes.votes || {});
          setHasSubmitted(playerVotes.submitted || false);
        }

        // Check if all players have voted
        const playerIds = players?.map(p => p.odplayerId) || [];
        const votedStatus = await checkAllPlayersVoted(competitionId, playerIds);
        setAllPlayersStatus(votedStatus);

        // If all players voted, load results
        if (votedStatus.allVoted && playerIds.length > 0) {
          const results = await getVotingResultsWithGameDetails(competitionId, sections, activeCats);
          setVotingResults(results);
        }

      } catch (err) {
        console.error('Error loading voting data:', err);
        setError('Failed to load voting data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [competition, competitionId, playerId, players]);

  // Save votes as draft (without submitting)
  const handleSaveDraft = useCallback(async (newVotes) => {
    if (!playerId || hasSubmitted) return;

    try {
      setSaving(true);
      await savePlayerVotes(competitionId, playerId, playerName, newVotes, false);
    } catch (err) {
      console.error('Error saving draft:', err);
    } finally {
      setSaving(false);
    }
  }, [competitionId, playerId, playerName, hasSubmitted]);

  // Add game to category votes
  const addGameToVotes = (categoryId, gameSectionId) => {
    if (hasSubmitted) return;

    setVotes(prev => {
      const categoryVotes = prev[categoryId] || [];
      if (categoryVotes.length >= 3) {
        setError('You can only select 3 games per category');
        setTimeout(() => setError(''), 3000);
        return prev;
      }
      if (categoryVotes.includes(gameSectionId)) {
        return prev;
      }
      const newVotes = {
        ...prev,
        [categoryId]: [...categoryVotes, gameSectionId]
      };
      handleSaveDraft(newVotes);
      return newVotes;
    });
  };

  // Remove game from category votes
  const removeGameFromVotes = (categoryId, gameSectionId) => {
    if (hasSubmitted) return;

    setVotes(prev => {
      const categoryVotes = prev[categoryId] || [];
      const newVotes = {
        ...prev,
        [categoryId]: categoryVotes.filter(id => id !== gameSectionId)
      };
      handleSaveDraft(newVotes);
      return newVotes;
    });
  };

  // Move game up in ranking
  const moveUp = (categoryId, index) => {
    if (hasSubmitted || index === 0) return;

    setVotes(prev => {
      const categoryVotes = [...(prev[categoryId] || [])];
      [categoryVotes[index - 1], categoryVotes[index]] = [categoryVotes[index], categoryVotes[index - 1]];
      const newVotes = { ...prev, [categoryId]: categoryVotes };
      handleSaveDraft(newVotes);
      return newVotes;
    });
  };

  // Move game down in ranking
  const moveDown = (categoryId, index) => {
    if (hasSubmitted) return;

    setVotes(prev => {
      const categoryVotes = [...(prev[categoryId] || [])];
      if (index >= categoryVotes.length - 1) return prev;
      [categoryVotes[index], categoryVotes[index + 1]] = [categoryVotes[index + 1], categoryVotes[index]];
      const newVotes = { ...prev, [categoryId]: categoryVotes };
      handleSaveDraft(newVotes);
      return newVotes;
    });
  };

  // Check if all categories have 3 votes
  const allCategoriesComplete = useMemo(() => {
    return categories.every(cat => {
      const categoryVotes = votes[cat.id] || [];
      const availableGames = gamesByCategory[cat.id]?.length || 0;
      // If category has less than 3 games, require all of them
      const requiredVotes = Math.min(3, availableGames);
      return categoryVotes.length >= requiredVotes;
    });
  }, [categories, votes, gamesByCategory]);

  // Submit final votes
  const handleSubmit = async () => {
    if (!allCategoriesComplete) {
      setError('Please select top 3 games for each category');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await savePlayerVotes(competitionId, playerId, playerName, votes, true);
      await submitPlayerVotes(competitionId, playerId);
      setHasSubmitted(true);
      setSuccessMessage('Your votes have been submitted!');

      // Reload voting status
      const playerIds = players?.map(p => p.odplayerId) || [];
      const votedStatus = await checkAllPlayersVoted(competitionId, playerIds);
      setAllPlayersStatus(votedStatus);

      if (votedStatus.allVoted && playerIds.length > 0) {
        const results = await getVotingResultsWithGameDetails(competitionId, gameSections, categories);
        setVotingResults(results);
      }

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error submitting votes:', err);
      setError('Failed to submit votes: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Organizer actions
  const handleCloseVoting = async () => {
    try {
      await closeVoting(competitionId);
      await refreshCompetition();
      setVotingStatus(prev => ({ ...prev, votingOpen: false }));
      setSuccessMessage('Voting closed');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to close voting: ' + err.message);
    }
  };

  const handleConfirmSchedule = async () => {
    try {
      await confirmSchedule(competitionId);
      await refreshCompetition();
      setVotingStatus(prev => ({ ...prev, scheduleConfirmed: true }));
      setSuccessMessage('Schedule confirmed! Voting section will be removed.');
      setTimeout(() => {
        navigate(`/competition/${competitionId}`);
      }, 2000);
    } catch (err) {
      setError('Failed to confirm schedule: ' + err.message);
    }
  };

  const handleReopenVoting = async () => {
    try {
      await reopenVoting(competitionId);
      await refreshCompetition();
      setVotingStatus(prev => ({ ...prev, votingOpen: true, scheduleConfirmed: false }));
      setSuccessMessage('Voting reopened');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to reopen voting: ' + err.message);
    }
  };

  // Reset all votes (organizer action)
  const handleResetAllVotes = async () => {
    if (!window.confirm('Are you sure you want to reset ALL player votes? This cannot be undone.')) {
      return;
    }

    try {
      setResetting(true);
      await deleteAllVotesForCompetition(competitionId);

      // Reset local state
      setVotes({});
      setHasSubmitted(false);
      setVotingResults(null);
      setAllPlayersStatus({ allVoted: false, votedCount: 0, totalPlayers: players?.length || 0, submittedPlayerIds: [], pendingPlayerIds: players?.map(p => p.odplayerId) || [] });

      setSuccessMessage('All votes have been reset');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error resetting votes:', err);
      setError('Failed to reset votes: ' + err.message);
    } finally {
      setResetting(false);
    }
  };

  // Reset a specific player's vote (organizer action)
  const handleResetPlayerVote = async (playerToReset) => {
    const playerResetId = playerToReset.odplayerId;
    const playerResetName = playerToReset.displayName;

    if (!window.confirm(`Reset votes for ${playerResetName}?`)) {
      return;
    }

    try {
      setResetting(true);
      await deletePlayerVote(competitionId, playerResetId);

      // If resetting current user's vote, update local state
      if (playerResetId === playerId) {
        setVotes({});
        setHasSubmitted(false);
      }

      // Reload voting status
      const playerIds = players?.map(p => p.odplayerId) || [];
      const votedStatus = await checkAllPlayersVoted(competitionId, playerIds);
      setAllPlayersStatus(votedStatus);

      // Clear results if not all voted anymore
      if (!votedStatus.allVoted) {
        setVotingResults(null);
      }

      setSuccessMessage(`Votes reset for ${playerResetName}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error resetting player vote:', err);
      setError('Failed to reset player vote: ' + err.message);
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading voting...</p>
        </div>
      </div>
    );
  }

  if (!competition?.leagueId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <h2 className="text-xl font-bold mb-4">No Game Library</h2>
          <p className="text-gray-600 mb-4">This competition is not linked to a league with a game library.</p>
          <Button onClick={() => navigate(`/competition/${competitionId}`)}>
            Back to Competition
          </Button>
        </Card>
      </div>
    );
  }

  if (votingStatus?.scheduleConfirmed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <h2 className="text-xl font-bold mb-4">Voting Closed</h2>
          <p className="text-gray-600 mb-4">The schedule has been confirmed by the organizer.</p>
          <Button onClick={() => navigate(`/competition/${competitionId}`)}>
            Back to Competition
          </Button>
        </Card>
      </div>
    );
  }

  // Show results if all have voted
  const showResults = allPlayersStatus.allVoted && allPlayersStatus.totalPlayers > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <Button variant="outline" size="sm" onClick={() => navigate(`/competition/${competitionId}`)}>
            ← Back to Competition
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 mt-2">Vote for Games</h1>
          <p className="text-gray-600">{competition?.name}</p>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
            {successMessage}
          </div>
        )}

        {/* Voting Status */}
        <Card className="mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-gray-900">Voting Status</h3>
              <p className="text-gray-600">
                {allPlayersStatus.votedCount} of {allPlayersStatus.totalPlayers} players have submitted
              </p>
              {!showResults && (
                <p className="text-sm text-gray-500 mt-1">
                  Results will be visible after all players submit their votes
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {hasSubmitted && (
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                  Submitted
                </span>
              )}
              {!votingStatus?.votingOpen && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                  Voting Closed
                </span>
              )}
            </div>
          </div>

          {/* Player status list */}
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-gray-600 mb-2">Player Status:</p>
            <div className="flex flex-wrap gap-2">
              {players?.map(player => {
                const voted = allPlayersStatus.submittedPlayerIds?.includes(player.odplayerId);
                return (
                  <span
                    key={player.odplayerId}
                    className={`px-2 py-1 rounded text-sm ${
                      voted ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {player.displayName} {voted ? '✓' : ''}
                  </span>
                );
              })}
            </div>
          </div>
        </Card>

        {/* Organizer Controls */}
        {isOrganizer && (
          <Card className="mb-6 bg-yellow-50 border-yellow-200">
            <h3 className="font-bold text-yellow-900 mb-3">Organizer Controls</h3>
            <div className="flex flex-wrap gap-2">
              {votingStatus?.votingOpen ? (
                <Button variant="outline" onClick={handleCloseVoting}>
                  Close Voting
                </Button>
              ) : (
                <Button variant="outline" onClick={handleReopenVoting}>
                  Reopen Voting
                </Button>
              )}
              {showResults && (
                <Button onClick={handleConfirmSchedule}>
                  Confirm Schedule
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleResetAllVotes}
                disabled={resetting}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                {resetting ? 'Resetting...' : 'Reset All Votes'}
              </Button>
            </div>

            {/* Individual player reset options */}
            {allPlayersStatus.votedCount > 0 && (
              <div className="mt-4 pt-4 border-t border-yellow-200">
                <p className="text-sm text-yellow-800 mb-2">Reset individual player votes:</p>
                <div className="flex flex-wrap gap-2">
                  {players?.filter(p => allPlayersStatus.submittedPlayerIds?.includes(p.odplayerId)).map(player => (
                    <button
                      key={player.odplayerId}
                      onClick={() => handleResetPlayerVote(player)}
                      disabled={resetting}
                      className="px-2 py-1 text-xs bg-white border border-yellow-300 rounded hover:bg-red-50 hover:border-red-300 text-yellow-800 hover:text-red-600 transition-colors disabled:opacity-50"
                    >
                      Reset {player.displayName}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-yellow-800 mt-3">
              After confirming the schedule, the voting section will be removed from the competition.
            </p>
          </Card>
        )}

        {/* Results Section */}
        {showResults && votingResults && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Voting Results</h2>
            {Object.values(votingResults).map(categoryResult => (
              <Card key={categoryResult.categoryId} className="mb-4">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <span>{categoryResult.categoryIcon}</span>
                  {categoryResult.categoryName}
                </h3>
                <div className="space-y-2">
                  {categoryResult.games.slice(0, 5).map((game, index) => (
                    <div
                      key={game.gameSectionId}
                      className={`flex items-center gap-3 p-3 rounded-lg ${
                        index < 3 ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        index === 0 ? 'bg-yellow-500 text-white' :
                        index === 1 ? 'bg-gray-400 text-white' :
                        index === 2 ? 'bg-amber-600 text-white' :
                        'bg-gray-200 text-gray-600'
                      }`}>
                        {index + 1}
                      </div>

                      {game.photoUrl ? (
                        <img
                          src={game.photoUrl}
                          alt={game.gameName}
                          className="w-10 h-10 rounded object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center">
                          <span className="text-xl opacity-50">🎲</span>
                        </div>
                      )}

                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{game.gameName}</h4>
                      </div>

                      <div className="text-right">
                        <div className="font-bold text-blue-600">{game.points} pts</div>
                        <div className="text-xs text-gray-500">
                          {game.firstPlaceVotes > 0 && `${game.firstPlaceVotes}x 1st`}
                          {game.secondPlaceVotes > 0 && ` ${game.secondPlaceVotes}x 2nd`}
                          {game.thirdPlaceVotes > 0 && ` ${game.thirdPlaceVotes}x 3rd`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Voting Interface */}
        {!showResults && votingStatus?.votingOpen !== false && (
          <>
            {/* Instructions */}
            <Card className="mb-6 bg-blue-50 border-blue-200">
              <h3 className="font-bold text-blue-900 mb-2">How to Vote</h3>
              <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
                <li>Select your top 3 games for each category</li>
                <li>Use arrows to reorder (1st = most wanted)</li>
                <li>Your votes are saved automatically as drafts</li>
                <li>Click "Submit Votes" when you're ready</li>
                <li>You cannot change votes after submitting</li>
              </ol>
            </Card>

            {/* Category Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-4 mb-4">
              {categories.map(cat => {
                const categoryVotes = votes[cat.id] || [];
                const availableGames = gamesByCategory[cat.id]?.length || 0;
                const requiredVotes = Math.min(3, availableGames);
                const isComplete = categoryVotes.length >= requiredVotes;

                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors flex items-center gap-2 ${
                      activeCategory === cat.id
                        ? 'text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    style={{
                      backgroundColor: activeCategory === cat.id ? cat.color : undefined
                    }}
                  >
                    <span>{cat.icon}</span>
                    {cat.name}
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      isComplete
                        ? 'bg-green-200 text-green-800'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {categoryVotes.length}/{requiredVotes}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Active Category Content */}
            {activeCategory && (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Available Games */}
                <div>
                  <h3 className="font-bold text-gray-900 mb-3">
                    Available Games
                  </h3>
                  <div className="space-y-2">
                    {(gamesByCategory[activeCategory] || [])
                      .filter(g => !(votes[activeCategory] || []).includes(g.id))
                      .map(game => (
                        <Card
                          key={game.id}
                          className={`cursor-pointer transition-colors ${
                            hasSubmitted
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => !hasSubmitted && addGameToVotes(activeCategory, game.id)}
                        >
                          <div className="flex items-center gap-3">
                            {game.photoUrl ? (
                              <img
                                src={game.photoUrl}
                                alt={game.name}
                                className="w-12 h-12 rounded object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded bg-gray-200 flex items-center justify-center">
                                <span className="text-2xl opacity-50">🎲</span>
                              </div>
                            )}
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">{game.name}</h4>
                              {game.averageDurationMinutes && (
                                <p className="text-sm text-gray-500">~{game.averageDurationMinutes} min</p>
                              )}
                            </div>
                            {!hasSubmitted && <span className="text-blue-600 text-xl">+</span>}
                          </div>
                        </Card>
                      ))}
                    {(gamesByCategory[activeCategory] || []).length === 0 && (
                      <p className="text-gray-500 text-center py-4">No games in this category</p>
                    )}
                  </div>
                </div>

                {/* Your Selections */}
                <div>
                  <h3 className="font-bold text-gray-900 mb-3">
                    Your Top 3
                  </h3>
                  {(votes[activeCategory] || []).length === 0 ? (
                    <Card className="text-center py-8 bg-gray-50">
                      <p className="text-gray-500">Click games to add to your ranking</p>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      {(votes[activeCategory] || []).map((gameId, index) => {
                        const game = gameSections.find(g => g.id === gameId);
                        if (!game) return null;

                        return (
                          <Card key={gameId} className="bg-white">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                index === 0 ? 'bg-yellow-500 text-white' :
                                index === 1 ? 'bg-gray-400 text-white' :
                                'bg-amber-600 text-white'
                              }`}>
                                {index + 1}
                              </div>

                              {game.photoUrl ? (
                                <img
                                  src={game.photoUrl}
                                  alt={game.name}
                                  className="w-10 h-10 rounded object-cover"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center">
                                  <span className="text-xl opacity-50">🎲</span>
                                </div>
                              )}

                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-900 truncate">{game.name}</h4>
                              </div>

                              {!hasSubmitted && (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => moveUp(activeCategory, index)}
                                    disabled={index === 0}
                                    className={`p-1 rounded ${
                                      index === 0 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                  >
                                    ▲
                                  </button>
                                  <button
                                    onClick={() => moveDown(activeCategory, index)}
                                    disabled={index >= (votes[activeCategory]?.length || 0) - 1}
                                    className={`p-1 rounded ${
                                      index >= (votes[activeCategory]?.length || 0) - 1
                                        ? 'text-gray-300'
                                        : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                  >
                                    ▼
                                  </button>
                                  <button
                                    onClick={() => removeGameFromVotes(activeCategory, gameId)}
                                    className="p-1 rounded text-red-500 hover:bg-red-50 ml-2"
                                  >
                                    ✕
                                  </button>
                                </div>
                              )}
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Submit Button */}
            {!hasSubmitted && (
              <div className="mt-6">
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !allCategoriesComplete}
                  className="w-full"
                >
                  {submitting ? 'Submitting...' : 'Submit Votes'}
                </Button>
                {!allCategoriesComplete && (
                  <p className="text-sm text-gray-500 text-center mt-2">
                    Complete all categories to submit
                  </p>
                )}
                {saving && (
                  <p className="text-sm text-gray-400 text-center mt-2">
                    Saving draft...
                  </p>
                )}
              </div>
            )}

            {hasSubmitted && (
              <Card className="mt-6 bg-green-50 border-green-200 text-center">
                <p className="text-green-700 font-medium">
                  Your votes have been submitted! Waiting for other players...
                </p>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GameVoting;
